import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { commissionPackagesAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Package, Plus, Pencil, Trash2, Loader2, Save } from 'lucide-react';

const CommissionPackages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', status: 'active' });

  useEffect(() => { fetchItems(); }, []);
  const fetchItems = async () => { try { const r = await commissionPackagesAPI.list(); setItems(r.data || []); } catch {} finally { setLoading(false); } };
  const reset = () => { setForm({ name: '', description: '', status: 'active' }); setEditId(null); };
  const openEdit = (i) => { setForm({ name: i.name, description: i.description || '', status: i.status }); setEditId(i.id); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name) { toast.error('Package name is required'); return; }
    setSaving(true);
    try {
      if (editId) await commissionPackagesAPI.update(editId, form); else await commissionPackagesAPI.create(form);
      toast.success(editId ? 'Package updated' : 'Package created'); fetchItems(); setDialogOpen(false); reset();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const handleDelete = async (id) => { if (!window.confirm('Delete this package?')) return; try { await commissionPackagesAPI.delete(id); toast.success('Deleted'); fetchItems(); } catch { toast.error('Failed'); } };
  const handleToggle = async (i) => { try { await commissionPackagesAPI.update(i.id, { status: i.status === 'active' ? 'inactive' : 'active' }); fetchItems(); } catch {} };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="commission-packages-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Package className="w-6 h-6" style={{ color: '#4680ff' }} />Commission Packages</h1>
            <p className="text-muted-foreground">Create and manage commission package presets</p>
          </div>
          <Button onClick={() => { reset(); setDialogOpen(true); }} className="bg-[#4680ff] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/20 rounded-lg h-10 px-5 text-sm font-semibold btn-press" data-testid="add-package-btn"><Plus className="w-4 h-4 mr-2" />Add Package</Button>
        </div>

        <Card><CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1, 2].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
            : items.length === 0 ? <div className="text-center py-12 text-muted-foreground"><Package className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No commission packages created</p><p className="text-sm mt-1">Create packages like "Gold", "Silver", "Bronze" to assign different commission rates</p></div>
              : <Table><TableHeader><TableRow><TableHead>Package Name</TableHead><TableHead>Description</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{items.map(i => (
                  <TableRow key={i.id}>
                    <TableCell className="font-semibold">{i.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[300px] truncate">{i.description || '-'}</TableCell>
                    <TableCell><button onClick={() => handleToggle(i)}><Badge className={i.status === 'active' ? 'bg-green-100 text-green-800 cursor-pointer' : 'bg-red-100 text-red-800 cursor-pointer'}>{i.status}</Badge></button></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(i)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(i.id)}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}</TableBody></Table>}
        </CardContent></Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}><DialogContent data-testid="package-dialog"><DialogHeader><DialogTitle>{editId ? 'Edit' : 'Add'} Commission Package</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Package Name *</Label><Input placeholder="e.g., Gold, Silver, Bronze" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="pkg-name" /></div>
            <div className="space-y-2"><Label>Description</Label><textarea rows={3} placeholder="Describe the commission package..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="pkg-desc" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button onClick={handleSave} disabled={saving} className="bg-[#4680ff] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/20 rounded-lg h-10 px-5 text-sm font-semibold btn-press" data-testid="save-pkg-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{editId ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent></Dialog>
      </div>
    </DashboardLayout>
  );
};
export default CommissionPackages;
